import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowDownLeft } from "lucide-react";

export default function RequestMoneyModal({ open, onClose, onSuccess }) {
  const { t } = useApp();
  const [payer, setPayer] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!payer.trim() || !amt || amt <= 0) return;
    setLoading(true);
    try {
      const res = await api.post("/requests", { payer: payer.trim(), amount: amt, note });
      toast.success(`${t.toastRequested} · ${res.data.payer}`);
      setPayer(""); setAmount(""); setNote("");
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="request-money-modal" className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowDownLeft className="w-5 h-5 text-primary" /> {t.requestMoney}
          </DialogTitle>
          <DialogDescription>{t.requestMoneyDesc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 pt-1">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.requestFrom}</label>
            <Input
              data-testid="request-payer-input"
              value={payer}
              onChange={(e) => setPayer(e.target.value)}
              placeholder={t.recipientPlaceholder}
              className="h-12 rounded-2xl mt-1.5"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.amount}</label>
            <div className="relative mt-1.5">
              <Input
                data-testid="request-amount-input"
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={t.enterAmount}
                className="h-12 rounded-2xl text-lg font-bold tabular-nums"
              />
              <span className="absolute top-1/2 -translate-y-1/2 end-4 text-sm font-bold text-muted-foreground">{t.currency}</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.note}</label>
            <Input
              data-testid="request-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t.notePlaceholder}
              className="h-12 rounded-2xl mt-1.5"
            />
          </div>
          <Button
            data-testid="request-money-form-submit"
            onClick={submit}
            disabled={loading || !payer || !amount}
            className="w-full h-[52px] rounded-2xl text-base font-bold"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.request}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
